import { useCallback, useEffect, useMemo, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { Fragment, type ReactNode } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useBusinessId, useTenantParams } from '@/hooks/use-tenant-params';
import { ArrowLeft, CloudOff, Eye, ExternalLink, Loader2, Redo2, RotateCcw, Search as SearchIcon, Undo2 } from 'lucide-react';
import { ulid } from 'ulid';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/skeleton';
import { EmptyState } from '@/components/empty-state';
import { EditableText } from '@/components/editable-text';
import { PublishCelebration } from '@/components/publish-celebration';
import { BlockPicker } from '@/components/block-picker';
import {
  SectionShell,
  allowedBlockTypesForColumn,
  parseColumnDroppableId,
} from '@/components/section-shell';
import { InsertSectionPoint } from '@/components/insert-section-point';
import {
  EmptyPageChooser,
  type ChooserAction,
} from '@/components/empty-page-chooser';
import {
  AiChatModal,
  type AiChatWireMessage,
  type AiTurnResult,
} from '@/components/ai-chat-modal';
import { StarterStackModal } from '@/components/starter-stack-modal';
import { TemplatePickerModal } from '@/components/template-picker-modal';
import { BlockPreview } from '@/components/block-preview';
import { InspectorHost } from '@/components/inspector/inspector-host';
import { SeoDrawer } from '@/components/seo-drawer';
import { useMe } from '@/hooks/use-me';
import { locationRoutes } from '@/lib/route-map';
import { UpgradeGate, nextPaidPlan, hasNextPaidPlan } from '@/components/upgrade-gate';
import {
  formatSavedAt,
  loadPage,
  usePageAutosave,
  type UsePageAutosaveReturn,
} from '@/hooks/use-page-autosave';
import {
  blockDefaults,
  blockTypeMeta,
  getStarterStackVariant,
  getTemplate,
  type Block,
  type BlockType,
  type LayoutTemplateId,
  type PageContentV2,
  type Section,
  type SectionSplit,
  type StarterStackVariantId,
} from '@pageboss/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { BlockOnWire, PageDetail, PageMutationResponse, SectionOnWire } from '@/lib/types';

type PickableTemplate = Exclude<
  LayoutTemplateId,
  'legacy-stack' | 'starter-stack' | 'ai-generated' | 'sub-page-default'
>;
type ChooserModal = 'ai' | 'starter-stack' | 'browse-layouts' | null;

interface BlockLocation {
  sectionId: string;
  columnIndex: number;
  blockIndex: number;
}

// Scan a sections tree for the first block with the given ID; returns
// its addr (section + column + index) or null if not found.
function locateBlock(sections: SectionOnWire[], blockId: string): BlockLocation | null {
  for (const section of sections) {
    for (let col = 0; col < section.columns.length; col++) {
      const column = section.columns[col]!;
      const idx = column.findIndex((b) => b.id === blockId);
      if (idx !== -1) {
        return { sectionId: section.id, columnIndex: col, blockIndex: idx };
      }
    }
  }
  return null;
}

const ALL_BLOCK_TYPES: readonly BlockType[] = blockTypeMeta.map((m) => m.type);

// Custom collision detection: when the pointer is directly over one or
// more droppables, prefer block-level (sortable items) over column-level
// wrappers. Otherwise fall back to closestCorners. This eliminates the
// overlap-conflict where a block sits inside a droppable column wrapper
// and dnd-kit's default collision detector occasionally picks the wrapper
// (yielding an imprecise "append" instead of an insertion-at-target).
const sectionAwareCollision: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) {
    const nonColumn = pointer.filter((c) => !String(c.id).startsWith('col:'));
    if (nonColumn.length > 0) return nonColumn;
    return pointer;
  }
  return closestCorners(args);
};

export function PageEdit() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();
  const { pageId } = useParams<{ pageId?: string }>();
  const [initialPage, setInitialPage] = useState<PageDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // A page read is Location-scoped: both halves of the canonical route are
    // required, and the load is re-keyed on both below.
    if (!businessId || !siteId || !pageId) return;
    let cancelled = false;
    loadPage(businessId, siteId, pageId)
      .then((p) => {
        if (!cancelled) setInitialPage(p);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId, pageId]);

  if (meState.kind === 'error') {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <EmptyState title="Something went wrong" description={meState.message} />
      </div>
    );
  }
  if (!siteId || !pageId) {
    return (
      <div className="mx-auto max-w-prose px-6 py-16">
        <EmptyState title="Page not found" description="Open a site, then a page to edit." />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="mx-auto flex min-h-screen max-w-prose flex-col px-6 py-16">
        <EmptyState
          title="Couldn't load this page"
          description={loadError}
          action={
            <Link to={locationRoutes.website(businessId, siteId)}>
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4" />
                Back to site
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  if (!initialPage) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="sticky top-0 z-10 h-14 border-b border-border bg-card/95">
          <div className="mx-auto flex h-full max-w-7xl items-center gap-4 px-6">
            <Skeleton className="h-4 w-32" />
            <div className="flex-1" />
            <Skeleton className="h-8 w-24" />
          </div>
        </header>
        <main className="hidden flex-1 px-6 py-10 md:block">
          <div className="mx-auto max-w-3xl">
            <PageBlocksSkeleton />
          </div>
        </main>
        <MobileFallback />
      </div>
    );
  }

  return (
    <PageEditLoaded
      // Remount the editor (and reset the autosave hook) whenever
      // `initialPage.updatedAt` changes — e.g. after a template pick
      // PATCH replaces the page contents wholesale.
      key={initialPage.updatedAt}
      initialPage={initialPage}
      siteId={siteId}
      pageId={pageId}
      onPageReplaced={setInitialPage}
    />
  );
}

interface PickerState {
  anchorRect: DOMRect;
  sectionId: string;
  columnIndex: number;
  insertIndex: number;
  allowedTypes: readonly BlockType[];
}

function PageEditLoaded({
  initialPage,
  siteId,
  pageId,
  onPageReplaced,
}: {
  initialPage: PageDetail;
  siteId: string;
  pageId: string;
  onPageReplaced: (page: PageDetail) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const autosave = usePageAutosave(initialPage, { siteId, pageId });
  const meState = useMe();
  const location = useLocation();
  const [showCelebration, setShowCelebration] = useState(false);
  // Site-lifecycle #19, Task 1 (fix round 1): whether the underlying SITE is
  // actually reachable — `autosave.page.siteStatus` is present on the
  // INITIAL page load too (not only after a publish click), so this is a
  // single source of truth for both the persistent top-bar badge/link and
  // the post-publish celebration copy.
  const siteLaunched = autosave.page.siteStatus === 'published';
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [seoOpen, setSeoOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link consumer (3B-3l) — reads ?focusBlock={id} and ?focusSeo
  // query params on first mount. Used by the SEO audit panel's per-
  // issue links to jump operators straight to the relevant editing
  // surface. Params are stripped after consumption so a refresh
  // doesn't re-focus.
  useEffect(() => {
    const focusBlock = searchParams.get('focusBlock');
    const focusSeo = searchParams.get('focusSeo');
    if (!focusBlock && !focusSeo) return;

    if (focusBlock) setSelectedBlockId(focusBlock);
    if (focusSeo) setSeoOpen(true);

    // Strip the consumed params from the URL so navigation history
    // doesn't carry them. replace:true so back-button doesn't return
    // to the focus state.
    const next = new URLSearchParams(searchParams);
    next.delete('focusBlock');
    next.delete('focusSeo');
    setSearchParams(next, { replace: true });

    if (focusBlock) {
      // Defer scroll until the canvas has painted with the new
      // selection — selectedBlockId triggers the sage focus ring on
      // the <li data-block-id> that we then center in viewport.
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-block-id="${focusBlock}"]`);
        if (el instanceof HTMLElement) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
    // Run-once on mount: deps intentionally minimal. Subsequent
    // setSearchParams calls (e.g. from elsewhere) don't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pickerState, setPickerState] = useState<PickerState | null>(null);
  const [chooserModal, setChooserModal] = useState<ChooserModal>(null);
  const [chooserPending, setChooserPending] = useState<ChooserModal>(null);
  const [chooserError, setChooserError] = useState<string | null>(null);
  const [activeDragBlock, setActiveDragBlock] = useState<BlockOnWire | null>(null);
  const wasPublished = initialPage.published;

  const sections = autosave.page.content.sections;
  // Show the empty-page chooser ONLY for the HOME page when its layout
  // hasn't been committed yet. Sub-pages skip the chooser entirely —
  // every chooser option is landing-page-shaped (hero+about+contact+
  // footer) which assumes wrong things about a sub-page like "Flavors"
  // or "About". Sub-pages are seeded server-side with a generic
  // skeleton (hero + body + cta + footer) on creation and the owner
  // edits in place.
  const showChooser =
    autosave.page.slug === 'home' && autosave.page.layoutTemplate === null;

  // Cmd/Ctrl+Z → undo, Cmd+Shift+Z / Ctrl+Y → redo. We bind at the
  // window level and rely on `usePageAutosave` to be tolerant when the
  // stacks are empty (no-op). Native contenteditable undo in
  // EditableText is broken anyway (React's useEffect overwrites the
  // user's undo result), so intercepting Cmd+Z app-wide is the right
  // behavior — the app-level undo can take the user back to before
  // they started typing in a field.
  const { undo, redo, canUndo, canRedo } = autosave;
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Esc closes the inspector when one is open. Does not steal Esc
      // from native popups because Esc is only intercepted when a
      // block is currently selected.
      if (e.key === 'Escape' && selectedBlockId) {
        e.preventDefault();
        setSelectedBlockId(null);
        return;
      }
      // Don't fight system shortcuts in dialog/native popups.
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 'z' || e.key === 'Z') {
        if (e.shiftKey) {
          if (!canRedo) return;
          e.preventDefault();
          redo();
        } else {
          if (!canUndo) return;
          e.preventDefault();
          undo();
        }
      } else if ((e.key === 'y' || e.key === 'Y') && !e.shiftKey) {
        if (!canRedo) return;
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, canUndo, canRedo, selectedBlockId]);

  async function handlePublish() {
    try {
      await autosave.publish();
      if (!autosave.page.published || !wasPublished) {
        setShowCelebration(true);
      }
    } catch {
      // hook surfaces error via status
    }
  }

  async function handlePreview() {
    try {
      const res = await api.get<{ url?: string; disabled?: boolean }>(
        locationApi(businessId, siteId, `preview-url?page=${encodeURIComponent(autosave.page.slug)}`),
      );
      if (res.url) window.open(res.url, '_blank', 'noopener');
    } catch {
      // best-effort — a failed preview is non-fatal
    }
  }

  // ---- Block-level handlers passed into SectionShell ---------------

  const onChangeBlock = useCallback(
    (sectionId: string, columnIndex: number, blockId: string, nextProps: Record<string, unknown>) => {
      autosave.updateBlockProps(sectionId, columnIndex, blockId, nextProps);
    },
    [autosave],
  );

  const onMoveBlockUp = useCallback(
    (sectionId: string, columnIndex: number, blockIndex: number) => {
      if (blockIndex <= 0) return;
      autosave.moveBlockWithinColumn(sectionId, columnIndex, blockIndex, blockIndex - 1);
    },
    [autosave],
  );

  const onMoveBlockDown = useCallback(
    (sectionId: string, columnIndex: number, blockIndex: number) => {
      const section = sections.find((s) => s.id === sectionId);
      const col = section?.columns[columnIndex];
      if (!col || blockIndex >= col.length - 1) return;
      autosave.moveBlockWithinColumn(sectionId, columnIndex, blockIndex, blockIndex + 1);
    },
    [autosave, sections],
  );

  const onDuplicateBlock = useCallback(
    (sectionId: string, columnIndex: number, blockId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      const col = section?.columns[columnIndex];
      if (!col) return;
      const idx = col.findIndex((b) => b.id === blockId);
      if (idx === -1) return;
      const source = col[idx]!;
      const copy: BlockOnWire = { ...source, id: ulid() };
      autosave.insertBlock(sectionId, columnIndex, idx + 1, copy);
      setSelectedBlockId(copy.id);
    },
    [autosave, sections],
  );

  const onDeleteBlock = useCallback(
    (sectionId: string, columnIndex: number, blockId: string) => {
      autosave.removeBlock(sectionId, columnIndex, blockId);
      // Try to select the next block (or previous) so editing flow continues.
      const section = sections.find((s) => s.id === sectionId);
      const col = section?.columns[columnIndex];
      if (!col) {
        setSelectedBlockId(null);
        return;
      }
      const idx = col.findIndex((b) => b.id === blockId);
      const fallback = col[idx + 1] ?? col[idx - 1] ?? null;
      setSelectedBlockId(fallback?.id ?? null);
    },
    [autosave, sections],
  );

  // ---- Section-level handlers --------------------------------------

  const onSplitRow = useCallback(
    (sectionId: string, nextSplit: SectionSplit) => {
      autosave.splitSection(sectionId, nextSplit);
    },
    [autosave],
  );

  const onMergeRow = useCallback(
    (sectionId: string) => {
      autosave.mergeSection(sectionId);
    },
    [autosave],
  );

  const onDeleteSection = useCallback(
    (sectionId: string) => {
      autosave.removeSection(sectionId);
      setSelectedBlockId(null);
    },
    [autosave],
  );

  // ---- Insert flow handlers ---------------------------------------

  // Multi-location phase (Task 9, Scenario B) — the `locations` block is only
  // offered when THIS site's own group has 2+ members (PageDetail carries the
  // eligibility flag, computed server-side in pages.ts's loadOwnedPage; the
  // client never re-derives it from raw group data, so there's no tenancy
  // surface here). An ineligible site sees the same 14-block library it
  // always has — no visual change, no dead entry in the picker.
  const availableBlockTypes = useMemo(
    () =>
      autosave.page.locationsBlockEligible
        ? ALL_BLOCK_TYPES
        : ALL_BLOCK_TYPES.filter((t) => t !== 'locations'),
    [autosave.page.locationsBlockEligible],
  );

  const requestPicker = useCallback(
    (
      sectionId: string,
      columnIndex: number,
      insertIndex: number,
      anchorRect: DOMRect,
    ) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      const allowedTypes = allowedBlockTypesForColumn(
        section.split,
        columnIndex,
        availableBlockTypes,
      );
      setPickerState({ anchorRect, sectionId, columnIndex, insertIndex, allowedTypes });
    },
    [sections, availableBlockTypes],
  );

  function closePicker() {
    setPickerState(null);
  }

  function onPickerPick(type: BlockType) {
    if (!pickerState) return;
    const newBlock: BlockOnWire = {
      id: ulid(),
      type,
      props: { ...(blockDefaults[type] as Record<string, unknown>) },
    };
    autosave.insertBlock(
      pickerState.sectionId,
      pickerState.columnIndex,
      pickerState.insertIndex,
      newBlock,
    );
    setSelectedBlockId(newBlock.id);
    closePicker();
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-block-id="${newBlock.id}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  // ---- Insert section ----------------------------------------------

  const insertSectionAt = useCallback(
    (atIndex: number, split: SectionSplit) => {
      autosave.insertSection(atIndex, split);
    },
    [autosave],
  );

  // ---- Drag and drop ----------------------------------------------

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragStart(event: DragStartEvent) {
    const id = String(event.active.id);
    const loc = locateBlock(sections, id);
    if (!loc) {
      setActiveDragBlock(null);
      return;
    }
    const section = sections.find((s) => s.id === loc.sectionId);
    const block = section?.columns[loc.columnIndex]?.[loc.blockIndex] ?? null;
    setActiveDragBlock(block);
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveDragBlock(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const source = locateBlock(sections, activeId);
    if (!source) return;

    // First check if `over` is a column droppable (empty column or drop
    // zone in a column with no specific block under the pointer).
    const columnDrop = parseColumnDroppableId(overId);
    if (columnDrop) {
      // Append to end of target column (or, when same section/column,
      // a within-column reorder isn't meaningful from this signal).
      if (
        columnDrop.sectionId === source.sectionId &&
        columnDrop.columnIndex === source.columnIndex
      ) {
        return;
      }
      autosave.moveBlockBetweenColumns(
        source.sectionId,
        source.columnIndex,
        columnDrop.sectionId,
        columnDrop.columnIndex,
        activeId,
      );
      return;
    }

    // Otherwise, `over` is presumably another block ID.
    const target = locateBlock(sections, overId);
    if (!target) return;

    if (
      source.sectionId === target.sectionId &&
      source.columnIndex === target.columnIndex
    ) {
      autosave.moveBlockWithinColumn(
        source.sectionId,
        source.columnIndex,
        source.blockIndex,
        target.blockIndex,
      );
      return;
    }

    // Cross-column same-section move (cross-section drag is out of
    // scope for 3.5c — guard so a stray over-id doesn't accidentally
    // move a block between unrelated sections).
    if (source.sectionId !== target.sectionId) return;

    autosave.moveBlockBetweenColumns(
      source.sectionId,
      source.columnIndex,
      target.sectionId,
      target.columnIndex,
      activeId,
      target.blockIndex,
    );
  }

  function onCanvasClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) setSelectedBlockId(null);
  }

  // ---- Empty-page chooser flow ------------------------------------

  async function applyChooserPayload(payload: {
    content: PageContentV2;
    layoutTemplate: string;
  }) {
    const res = await api.patch<PageMutationResponse>(
      locationApi(businessId, siteId, `pages/${pageId}`),
      payload,
    );
    onPageReplaced(res.page);
  }

  async function applyStarterStack(variantId: StarterStackVariantId) {
    setChooserError(null);
    setChooserPending('starter-stack');
    try {
      await applyChooserPayload(buildStarterStackPayload(variantId));
    } catch (err) {
      setChooserPending(null);
      setChooserError(err instanceof Error ? err.message : 'Could not set up the starter stack.');
    }
  }

  async function applyTemplateFromModal(id: PickableTemplate) {
    setChooserError(null);
    setChooserPending('browse-layouts');
    try {
      await applyChooserPayload(buildTemplatePayloadFor(id));
    } catch (err) {
      setChooserPending(null);
      setChooserError(err instanceof Error ? err.message : 'Could not apply that layout.');
    }
  }

  // AI chat — 3B-3.5e live backend. POST the full conversation to
  // /ai-chat; the server appends one assistant turn (either a follow-up
  // question or the final generated page). Errors are converted into
  // a friendly assistant bubble so the modal can keep the conversation
  // alive instead of dying on a single failure.
  async function submitAiMessage(messages: AiChatWireMessage[]): Promise<AiTurnResult> {
    try {
      const res = await api.post<
        | { state: 'asking'; assistantMessage: string }
        | {
            state: 'done';
            assistantMessage: string;
            page: PageDetail;
            generationId: string;
          }
      >(locationApi(businessId, siteId, `pages/${pageId}/ai-chat`), { messages });
      return res;
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      let friendly = "Hmm, I couldn't generate this time. Try again, or close this and pick a starter stack.";
      let gate: ReactNode | undefined;
      try {
        const parsed = JSON.parse(raw) as { error?: string; limit?: number; tier?: string; message?: string };
        if (parsed.error === 'quota_exceeded') {
          const tier = meState.kind === 'ready' ? meState.me.usage.tier : (parsed.tier ?? 'free');
          if (hasNextPaidPlan(tier)) {
            friendly = `You've used all ${parsed.limit ?? ''} AI generations this month. Pick a starter stack to keep going without spending one, or upgrade for more.`;
            gate = (
              <UpgradeGate
                capability={`You've used all ${parsed.limit ?? ''} AI generations this month.`}
                planKey={nextPaidPlan(tier)}
                from={location.pathname}
              />
            );
          } else {
            friendly = `You've used all ${parsed.limit ?? ''} AI generations this month. On the Billing page you can buy 100 more for $9, or pick a starter stack to keep going.`;
          }
        } else if (parsed.error === 'ai_not_in_plan') {
          friendly = 'AI generation comes with any paid plan. Pick a starter stack or layout to build manually, or upgrade for AI generation.';
          gate = (
            <UpgradeGate
              capability="AI generation comes with any paid plan."
              planKey="pro"
              from={location.pathname}
            />
          );
        } else if (parsed.error === 'ai_not_configured') {
          friendly = 'AI is not configured on this dashboard yet. Pick a starter stack or a layout while we wire that up.';
        } else if (parsed.error === 'invalid_generation') {
          friendly = "I hit a snag with the JSON shape of the page. Try again with a bit more detail, or pick a starter stack.";
        } else if (parsed.error === 'ai_call_failed') {
          friendly = `I couldn't reach the AI service this time${parsed.message ? ` (${parsed.message.slice(0, 80)})` : ''}. Try again in a moment, or pick a starter stack.`;
        } else if (parsed.error === 'persistence_failed') {
          friendly = 'I wrote the page but couldn\'t save it. Try once more; the page itself is fine — it just didn\'t commit.';
        }
      } catch {
        // raw is not JSON; keep the generic friendly message.
      }
      return { state: 'error', message: friendly, gate };
    }
  }

  // Called by AiChatModal once the AI hands back the generated page.
  // Closes the modal and routes the new page through the same
  // onPageReplaced flow that starter-stack / browse-layouts already use.
  function applyAiGeneratedPage(page: PageDetail) {
    setChooserModal(null);
    onPageReplaced(page);
  }

  // Reset the page back to the empty-page chooser. Clears all blocks
  // and unsets `layoutTemplate` so the editor falls back to the
  // chooser branch on next render. Confirmation gate prevents
  // accidental loss of in-progress content.
  async function resetPage() {
    const hasContent = autosave.page.blocks.length > 0;
    const message = hasContent
      ? 'Start over? This clears everything on this page and brings back the starting options. Your other pages are untouched.'
      : 'Bring back the starting options for this page?';
    const ok = window.confirm(message);
    if (!ok) return;
    try {
      const res = await api.patch<PageMutationResponse>(
        locationApi(businessId, siteId, `pages/${pageId}`),
        { content: { version: 2, sections: [] }, layoutTemplate: null },
      );
      onPageReplaced(res.page);
    } catch (err) {
      setChooserError(err instanceof Error ? err.message : 'Could not reset the page.');
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <EditorTopBar
        siteId={siteId}
        autosave={autosave}
        onPublish={handlePublish}
        onPreview={() => void handlePreview()}
        onResetPage={() => void resetPage()}
        canResetPage={!showChooser}
        onOpenSeo={() => setSeoOpen(true)}
      />

      <MobileFallback />

      <div className="hidden flex-1 md:flex md:flex-row">
      <main className="flex-1 min-w-0">
        {showChooser ? (
          <>
            <EmptyPageChooser
              onPickAction={(action: ChooserAction) => setChooserModal(action)}
              pendingAction={chooserPending}
            />
            {chooserError ? (
              <div className="mx-auto max-w-4xl px-6 pb-12">
                <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
                  {chooserError}
                </div>
              </div>
            ) : null}
            <AiChatModal
              open={chooserModal === 'ai'}
              onClose={() => setChooserModal(null)}
              onSubmit={submitAiMessage}
              onPageGenerated={applyAiGeneratedPage}
            />
            <StarterStackModal
              open={chooserModal === 'starter-stack'}
              onClose={() => setChooserModal(null)}
              onPick={(variantId) => {
                setChooserModal(null);
                void applyStarterStack(variantId);
              }}
              pendingVariantId={null}
            />
            <TemplatePickerModal
              open={chooserModal === 'browse-layouts'}
              onClose={() => setChooserModal(null)}
              onPick={(id) => {
                setChooserModal(null);
                void applyTemplateFromModal(id);
              }}
              onPickStarterStack={() => {
                // From inside the layout modal, "None of these — start with a clean stack"
                // routes to the starter-stack picker, not directly to a fixed stack.
                setChooserModal('starter-stack');
              }}
              pendingTemplateId={null}
            />
          </>
        ) : (
          <div className="mx-auto max-w-5xl px-6 py-10" onClick={onCanvasClick}>
            {showCelebration ? (
              <div className="mb-6">
                <PublishCelebration
                  publicUrl={autosave.page.publicUrl}
                  onDismiss={() => setShowCelebration(false)}
                  launched={siteLaunched}
                />
              </div>
            ) : null}

            <DndContext
              sensors={sensors}
              collisionDetection={sectionAwareCollision}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragCancel={() => setActiveDragBlock(null)}
            >
              <ol className="space-y-6">
                {sections.map((section, sectionIdx) => (
                  <Fragment key={section.id}>
                    <li>
                      <InsertSectionPoint
                        variant="hover"
                        onPick={(split) => insertSectionAt(sectionIdx, split)}
                      />
                    </li>
                    <li>
                      <SectionShell
                        section={section}
                        selectedBlockId={selectedBlockId}
                        onSelectBlock={(id) => setSelectedBlockId(id)}
                        onChangeBlock={(columnIndex, blockId, nextProps) =>
                          onChangeBlock(section.id, columnIndex, blockId, nextProps)
                        }
                        onMoveBlockUp={(columnIndex, blockIndex) =>
                          onMoveBlockUp(section.id, columnIndex, blockIndex)
                        }
                        onMoveBlockDown={(columnIndex, blockIndex) =>
                          onMoveBlockDown(section.id, columnIndex, blockIndex)
                        }
                        onDuplicateBlock={(columnIndex, blockId) =>
                          onDuplicateBlock(section.id, columnIndex, blockId)
                        }
                        onDeleteBlock={(columnIndex, blockId) =>
                          onDeleteBlock(section.id, columnIndex, blockId)
                        }
                        onRequestPicker={(columnIndex, insertIndex, anchorRect) =>
                          requestPicker(section.id, columnIndex, insertIndex, anchorRect)
                        }
                        onSplitRow={(next) => onSplitRow(section.id, next)}
                        onMergeRow={() => onMergeRow(section.id)}
                        onDeleteSection={() => onDeleteSection(section.id)}
                      />
                    </li>
                  </Fragment>
                ))}
                <li>
                  <InsertSectionPoint
                    variant="always"
                    label="Add row at end"
                    onPick={(split) => insertSectionAt(sections.length, split)}
                  />
                </li>
              </ol>
              <DragOverlay dropAnimation={null}>
                {activeDragBlock ? (
                  <div
                    aria-hidden
                    className="pointer-events-none cursor-grabbing rounded-xl border border-brand-500 bg-card p-6 opacity-95 shadow-lg ring-2 ring-brand-500"
                    style={{ maxWidth: '40rem' }}
                  >
                    <BlockPreview block={activeDragBlock as unknown as Block} />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </main>
        <InspectorHost
          sections={sections}
          selectedBlockId={selectedBlockId}
          onClose={() => setSelectedBlockId(null)}
          onChangeBlock={onChangeBlock}
        />
      </div>

      <BlockPicker
        open={pickerState !== null}
        anchorRect={pickerState?.anchorRect ?? null}
        allowedTypes={pickerState?.allowedTypes}
        onClose={closePicker}
        onPick={onPickerPick}
      />

      <SeoDrawer
        open={seoOpen}
        onClose={() => setSeoOpen(false)}
        pageTitle={autosave.page.title}
        pageSlug={autosave.page.slug}
        publicUrl={autosave.page.publicUrl}
        seoMeta={autosave.page.seoMeta}
        onChange={autosave.setSeoMeta}
        onCommitSlug={autosave.commitSlug}
      />
    </div>
  );
}

interface TopBarProps {
  siteId: string;
  autosave: UsePageAutosaveReturn;
  onPublish: () => void;
  onPreview: () => void;
  onResetPage: () => void;
  canResetPage: boolean;
  onOpenSeo: () => void;
}

function EditorTopBar({
  siteId,
  autosave,
  onPublish,
  onPreview,
  onResetPage,
  canResetPage,
  onOpenSeo,
}: TopBarProps) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  // Site-lifecycle #19, Task 1 (fix round 1): "live" requires BOTH the page
  // itself to be published AND the underlying site to be launched
  // (autosave.page.siteStatus === 'published') — a page can be published on
  // a still-draft site indefinitely, and this badge/link must not claim
  // reachability that isn't there (the "View live" link would 404).
  const pageLive = autosave.page.published && autosave.page.siteStatus === 'published';
  const pageSavedNotLaunched = autosave.page.published && !pageLive;
  return (
    <header className="sticky top-0 z-10 h-14 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="mx-auto flex h-full max-w-7xl items-center gap-4 px-6">
        <Link
          to={locationRoutes.website(businessId, siteId)}
          className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back to site</span>
        </Link>

        <div className="flex min-w-0 flex-1 items-center gap-3">
          <EditableText
            value={autosave.page.title}
            onChange={autosave.setTitle}
            placeholder="Untitled page"
            className="truncate text-sm font-semibold tracking-tight text-foreground"
            aria-label="Page title"
          />
          {/* Site-lifecycle #19, Task 1 (fix round 1): a published PAGE can
              still sit on an unlaunched (draft/paused/archived) SITE
              indefinitely — "Live" must mean the page is ACTUALLY publicly
              reachable, not just that its own published flag is set. */}
          <Badge
            variant={pageLive ? 'success' : pageSavedNotLaunched ? 'warning' : 'muted'}
            className="hidden shrink-0 sm:inline-flex"
            title={pageSavedNotLaunched ? "Saved — launch the site to make it public" : undefined}
          >
            {pageLive ? 'Live' : pageSavedNotLaunched ? 'Saved' : 'Draft'}
          </Badge>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <UndoRedoButtons autosave={autosave} />
          <SaveIndicator autosave={autosave} />
          {canResetPage ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onResetPage}
              className="hidden h-auto px-2 py-1 text-xs text-text-secondary md:inline-flex"
              title="Clear this page and bring back the starting options"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Start over
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            onClick={onOpenSeo}
            className="hidden h-auto px-2 py-1 text-xs text-text-secondary md:inline-flex"
            title="Page SEO settings (title, description, social share image)"
          >
            <SearchIcon className="h-3.5 w-3.5" />
            SEO
          </Button>
          {autosave.page.publicUrl && pageLive ? (
            <a
              href={autosave.page.publicUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground lg:inline-flex"
            >
              View live
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            onClick={onPreview}
            className="hidden h-auto px-2 py-1 text-xs text-text-secondary md:inline-flex"
            title="Open this page's current draft in a new tab (preview)"
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Button>
          <Button size="sm" onClick={onPublish} disabled={autosave.isPublishing}>
            {autosave.isPublishing ? 'Publishing…' : autosave.page.published ? 'Republish' : 'Publish'}
          </Button>
        </div>
      </div>
    </header>
  );
}

function UndoRedoButtons({ autosave }: { autosave: UsePageAutosaveReturn }) {
  const { undo, redo, canUndo, canRedo } = autosave;
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const undoHint = isMac ? '⌘Z' : 'Ctrl+Z';
  const redoHint = isMac ? '⇧⌘Z' : 'Ctrl+Y';
  return (
    <div className="hidden items-center gap-px rounded-md border border-border bg-card sm:inline-flex">
      <button
        type="button"
        onClick={undo}
        disabled={!canUndo}
        title={`Undo (${undoHint})`}
        aria-label={`Undo (${undoHint})`}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-l-md text-text-secondary',
          'transition-colors hover:bg-surface-sunken hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-secondary',
        )}
      >
        <Undo2 className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={redo}
        disabled={!canRedo}
        title={`Redo (${redoHint})`}
        aria-label={`Redo (${redoHint})`}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-r-md text-text-secondary',
          'transition-colors hover:bg-surface-sunken hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-secondary',
        )}
      >
        <Redo2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function SaveIndicator({ autosave }: { autosave: UsePageAutosaveReturn }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (autosave.status !== 'saved') return;
    const t = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(t);
  }, [autosave.status]);

  if (autosave.status === 'offline') {
    return (
      <span className="hidden items-center gap-1.5 text-xs text-warning-fg sm:inline-flex">
        <CloudOff className="h-3.5 w-3.5" />
        Offline · we'll save when you're back
      </span>
    );
  }
  if (autosave.status === 'error') {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={autosave.retryNow}
        className="h-auto bg-danger-bg px-2 py-1 text-xs text-danger-fg hover:bg-danger-bg/80 hover:text-danger-fg"
      >
        Couldn't save — retry
      </Button>
    );
  }
  if (autosave.status === 'saving' || autosave.status === 'dirty') {
    return (
      <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving…
      </span>
    );
  }
  return (
    <span className="hidden text-xs text-muted-foreground sm:inline">
      {formatSavedAt(autosave.savedAt, now)}
    </span>
  );
}

function PageBlocksSkeleton() {
  return (
    <ol className="space-y-5">
      {[0, 1, 2].map((i) => (
        <li key={i} className="rounded-xl border border-border-subtle bg-card p-6">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="mt-3 h-3 w-full" />
          <Skeleton className="mt-2 h-3 w-5/6" />
          <Skeleton className="mt-2 h-3 w-2/3" />
        </li>
      ))}
    </ol>
  );
}

function MobileFallback() {
  return (
    <div className="flex flex-1 items-center justify-center px-6 py-16 md:hidden">
      <EmptyState
        title="Open this page on a bigger screen to edit."
        description="Your site itself works great on phones — this is just the editor. Switch to a tablet or laptop to make changes."
      />
    </div>
  );
}

// Build the PATCH payload that seeds a page from one of the 6 named
// vertical-business templates (rendered behind the "Browse starting
// layouts" modal in 3B-3.5d+).
function buildTemplatePayloadFor(id: PickableTemplate): {
  content: PageContentV2;
  layoutTemplate: string;
} {
  const template = getTemplate(id);
  if (!template) {
    throw new Error(`Unknown template: ${id}`);
  }
  const sections: Section[] = template.sections.map((spec) => ({
    id: ulid(),
    slotKey: spec.slotKey,
    split: spec.split,
    columns: spec.columns.map((col) =>
      col.blockTypes.map(
        (type) =>
          ({
            id: ulid(),
            type,
            props: blockDefaults[type],
          }) as unknown as Block,
      ),
    ),
  }));
  return {
    content: { version: 2, layoutTemplate: id, sections },
    layoutTemplate: id,
  };
}

// Build the PATCH payload for one of the 3 starter-stack variants
// (minimal / standard / rich). Each variant lists block types; each
// type becomes its own single-column full-width section. Owner edits
// each block inline once the editor reloads.
function buildStarterStackPayload(variantId: StarterStackVariantId): {
  content: PageContentV2;
  layoutTemplate: string;
} {
  const variant = getStarterStackVariant(variantId);
  const sections: Section[] = variant.blockTypes.map((type) => ({
    id: ulid(),
    split: 'full',
    columns: [
      [
        {
          id: ulid(),
          type,
          props: blockDefaults[type],
        } as unknown as Block,
      ],
    ],
  }));
  return {
    content: { version: 2, layoutTemplate: 'starter-stack', sections },
    layoutTemplate: 'starter-stack',
  };
}
